const DB_NAME = 'aotf-landing-blob-gifs';
/** v2: recapture after wireframe-off GIF export fix (v1 wireframe GIFs were invisible). */
const DB_VERSION = 2;
const STORE = 'gifs';

/** In-memory GIF src per route key — avoids re-reading IndexedDB and object-URL flicker on remount. */
const sessionGifSrcByRouteKey = new Map();
const gifLoadInFlightByRouteKey = new Set();

export function beginLandingGifLoad(routeKey) {
  const key = String(routeKey);
  if (gifLoadInFlightByRouteKey.has(key)) return false;
  gifLoadInFlightByRouteKey.add(key);
  return true;
}

export function endLandingGifLoad(routeKey) {
  gifLoadInFlightByRouteKey.delete(String(routeKey));
}

export function getSessionLandingGifSrc(routeKey) {
  if (routeKey == null || routeKey === '') return null;
  return sessionGifSrcByRouteKey.get(String(routeKey)) ?? null;
}

export function rememberSessionLandingGifSrc(routeKey, src) {
  if (routeKey == null || routeKey === '' || !src) return src;
  const key = String(routeKey);
  const prev = sessionGifSrcByRouteKey.get(key);
  if (prev && prev.startsWith('blob:') && prev !== src) {
    URL.revokeObjectURL(prev);
  }
  sessionGifSrcByRouteKey.set(key, src);
  return src;
}

function openDb() {
  if (typeof indexedDB === 'undefined') {
    return Promise.reject(new Error('IndexedDB unavailable'));
  }
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error || new Error('IndexedDB open failed'));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      if (event.oldVersion > 0 && event.oldVersion < DB_VERSION && db.objectStoreNames.contains(STORE)) {
        db.deleteObjectStore(STORE);
      }
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
  });
}

export async function readLandingBlobGif(routeKey) {
  if (routeKey == null || routeKey === '') return null;
  try {
    const db = await openDb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(String(routeKey));
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function writeLandingBlobGif(routeKey, gifBlob) {
  if (routeKey == null || routeKey === '' || !gifBlob) return false;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).put(gifBlob, String(routeKey));
    });
    const url = URL.createObjectURL(gifBlob);
    rememberSessionLandingGifSrc(routeKey, url);
    return true;
  } catch {
    return false;
  }
}

export async function hasLandingBlobGif(routeKey) {
  const blob = await readLandingBlobGif(routeKey);
  return Boolean(blob);
}
